import cs from 'classnames';
import React from 'react';
import { parseAndFormatCurrency } from 'shared/utils/currency';
import { App_PortalFragment, CartItemFragment, Cart_ApplicationFragment } from '../../generated.graphql';
import styles from './Cart.module.scss';
import { useItem } from './hooks';
import { NewProductDescriptionButton } from './NewProductDescription';
import { PrelovedConditionReportButton } from './PrelovedConditionReport';
import { ProtectionPlanInfo, ProtectionPlanTerms } from './ProtectionPlan';
import { ProtectionPlanCheckbox, ProtectionPlanLabelledCheckbox } from './ProtectionPlanCheckbox';
import { PurchasePricingPopoverButton } from './PurchasePricing';
import { SuggestedItems } from './SuggestedItems';
import { RemoveItemBtn } from './RemoveItemBtn';
import { ProtectionPlanBenifits } from './ProtectionPlanBenefits';
import { ProtectionPlanLogo } from './ProtectionPlanLogo';
import { MinRentalPeriod } from './RebatePeriodSelect';

interface Columns {
  image: boolean
  condition: boolean
  quantity: boolean
  remove: boolean
}

interface Props extends React.HTMLAttributes<HTMLTableElement> {
  application: Cart_ApplicationFragment;
  portal: App_PortalFragment;
  columns: Columns;
}

const calcColSpan = (columns: Columns) => 2 + (columns.image ? 1 : 0) + (columns.condition ? 1 : 0);


export const CartTable = ({application, columns, className, ...props}: Props) => {
  const { pricing } = application;
  const colSpan = calcColSpan(columns);
  return (
  <table className={cs('table', 'table-borderless', styles.CartTable, className)} {...props}>
    <thead className="border-bottom">
      <tr>
        <th colSpan={columns.image ? 2 : 1} className="">Items to be rented</th>
        {columns.condition && <th className="">Condition</th>}
        <th className="text-center">{columns.quantity && 'Quantity'}</th>
        <th className="text-right">Monthly payment</th>
        {columns.remove && <th></th>}
      </tr>
    </thead>
    <tbody>
      {application.items?.map(item => 
        <CartTableItem item={item} key={item.id} columns={columns} readonly={!!application.inStore} />
      )}

      {application.suggestedItems && application.suggestedItems.length > 0 && (
        <tr className="border-top">
          <td colSpan={colSpan + 2}>
            <SuggestedItems items={application.suggestedItems} portalSlug={application.portal.slug} />
          </td>
        </tr>
      )}
        
      {pricing.protectionPlan &&
        <tr className="border-top">
          <td colSpan={(colSpan - 1)}>
            {/* <ProtectionPlanInfo/> */}
            <div className='d-flex align-items-start'>
              <ProtectionPlanLogo className='mr-4 py-1'/>
              <ProtectionPlanBenifits application={application} portalSlug={props.portal.slug}/>
            </div>
          </td>
          <td className='text-center'><ProtectionPlanCheckbox application={application} mode='switch'/></td>
          <td className="text-right">
            {/* {application.protectionPlan && parseAndFormatCurrency(application.protectionPlan)} */}
            {parseAndFormatCurrency(application.includesProtectionPlan ? pricing.protectionPlan : 0)}
          </td>
          <td></td>
        </tr>
      }
    </tbody>
    <tfoot className="border-top mt-3">
      <tr>
        <td className="text-right" colSpan={colSpan}>Regular monthly payments:</td>
        <td className="text-right">{parseAndFormatCurrency(pricing.ongoingPayment)}</td>
      </tr>
      <tr>
        <td className="text-right" colSpan={colSpan}>Once off {application.importantInfo.freightHeading.toLowerCase()}:</td>
        <td className="text-right">{parseAndFormatCurrency(pricing.setupFee)}</td>
      </tr>
    </tfoot>
  </table>
)};



export const CartTableItem = ({item, columns, readonly}: {item: CartItemFragment, columns: Columns, readonly: boolean}) => {
  const h = useItem({item, readonly})
  const colSpan = calcColSpan(columns);
  return (
    <>
      <tr style={h.style} className={cs(styles.CartItemRowMain, 'border-top')}>
        {columns.image && 
          <td rowSpan={2}>
              <div className={cs("bg-white shadow p-2 w-100", styles.ItemImageWidth)}>
                {item.imageUrl && (
                  <img src={item.imageUrl} className="img-fluid" alt="Item thumbnail"/>
                )}
              </div>
          </td>
        }
        <td rowSpan={1} className="pb-0 pl-0">
          <div className="d-inline-block h-100">
            <div className="d-flex flex-column h-100">
              <div className="mb-auto">
                <h5>{h.formattedName}</h5>
                <div className="small ">{item.subtitle}</div>
                <div className='mt-2'><MinRentalPeriod minMonths={item.selectedRate.minMonths ?? item.minMonths}/></div>
              </div>
              {/* <div><button className="btn btn-link px-0 py-0">Preloved condition report</button></div>
              <div><button className="btn btn-link px-0 py-0">Apply to purchase pricing</button></div> */}
            </div>
          </div>
        </td>
        {columns.condition && 
          <td rowSpan={1}>{h.formattedCondition}</td>
        }
        <td rowSpan={1} className="text-center">
          <h.QtySelect className={cs("form-control custom-select mx-auto text-center", styles.QtyInput)}/>
        </td>
        <td rowSpan={1} className="text-right">{h.formattedRate}</td>
        {columns.remove &&
          <td rowSpan={1} className="pl-3 text-right">
            <RemoveItemBtn 
              disabled={h.isRemoving}
              onRemove={async () => {await h.remove()}}
              className="btn btn-link px-2 py-0"
            />
          </td>
        }
      </tr>
      <tr style={h.style} className={cs(styles.CartItemRowSecondary)}>
        <td colSpan={colSpan} className="pb-0">
          <div className="d-flex flex-wrap flex-column align-content-start mb-4">
            
            <PurchasePricingPopoverButton rate={item.selectedRate} startValue={item.startValue} caret={true} className="btn btn-link p-0 mr-5 text-left"/>
            {item.newProductDescription && 
              <NewProductDescriptionButton item={item} caret={true} className="btn btn-link p-0 mr-5 text-left"/>}
            {item.prelovedConditionReport &&
              <PrelovedConditionReportButton item={item} caret={true} className="btn btn-link p-0 mr-5 text-left"/>}
          </div>
        </td>
      </tr>
    </>
  )
};
